import {
  Link,
  Text,
  Wrap,
} from '@chakra-ui/react';
import Chart from 'react-apexcharts';
import {
  useBridgeRenewalsAndNewBridges,
  useNewAndImprovedRoads,
} from '@src/modules/performance/context/reports-api-hooks';
import GroupedRegionMessage from '@src/modules/performance/components/GroupedRegionMessage';
import { CHART_COLOURS } from '@src/modules/performance/shared/constants';
import ReportSection from './ReportSection';

const informationSource = {
  title: 'Other Works Completed',
  content: [
    {
      title: 'Bridge renewals and new bridges (lane km)',
      description:
  <Text fontSize="inherit" mb={2}>Actual reported lane-km lengths of new bridges and bridge renewals.</Text>,
      source: null,
    },
    {
      title: 'New and improved roads (lane km)',
      description:
  <Text fontSize="inherit" mb={2}>Actual reported lengths achieved for new and improved roads.</Text>,
      source:
  <Text fontSize="inherit" as="span">
    Sourced from
    {' '}
    <Link href="https://www.nzta.govt.nz/planning-and-investment/learning-and-resources/transport-data/data-and-tools/" isExternal>NZTA Data and Tools</Link>
  </Text>,
    },
  ],
};

function RoadMaintenanceDeliveryOtherWorks() {
  const { data: newAndImprovedRoads, isLoading: isLoadingNewAndImproved } = useNewAndImprovedRoads();
  const { data: bridgeRenewalsAndNewBridges, isLoading: isLoadingBridgeRenewals } = useBridgeRenewalsAndNewBridges();
  const isLoading = isLoadingNewAndImproved || isLoadingBridgeRenewals;

  // Bridge Renewals and New Bridges Chart
  const bridgeRenewalsAndNewBridgesCategories = [...new Set(bridgeRenewalsAndNewBridges?.map((item) => item.Year))].sort((a, b) => a - b);
  const bridgeRenewalsAndNewBridgesData = [
    {
      name: 'New Bridges',
      type: 'bar',
      data: bridgeRenewalsAndNewBridgesCategories.map((year) => ({
        x: year,
        y: bridgeRenewalsAndNewBridges?.find((item) => item.Year === year)?.New_Bridges,
      })),
      color: CHART_COLOURS.seagreen,
    },
    {
      name: 'Bridge Renewals',
      type: 'bar',
      data: bridgeRenewalsAndNewBridgesCategories.map((year) => ({
        x: year,
        y: bridgeRenewalsAndNewBridges?.find((item) => item.Year === year)?.Bridge_Renewals,
      })),
      color: CHART_COLOURS.orange,
    },
  ];

  // New and Improved Roads Chart
  const newAndImprovedRoadsCategories = [...new Set(newAndImprovedRoads?.map((item) => item.Year))].sort((a, b) => a - b);
  const newAndImprovedRoadsData = [
    {
      name: 'New & Improved Roads',
      type: 'bar',
      data: newAndImprovedRoadsCategories.map((year) => ({
        x: year,
        y: newAndImprovedRoads?.find((item) => item.Year === year)?.Value,
      })),
      color: CHART_COLOURS.seagreen,
    },
  ];

  return (
    <ReportSection
      title="Other Works Completed"
      isLoading={isLoading}
      lockYear={newAndImprovedRoads?.[0]?.LockYear}
      informationSource={informationSource}
    >
      <Wrap spacing={4}>
        <Chart
          options={
            {
              title: {
                text: 'Bridge renewals & new bridges (lane km)',
              },
              chart: {
                animations: {
                  enabled: false,
                },
              },
              xaxis: {
                categories: bridgeRenewalsAndNewBridgesCategories,
                labels: {
                  offsetY: 5,
                  rotate: -45,
                  rotateAlways: true,
                },
                tickPlacement: 'between',
              },
              yaxis: [
                {
                  axisTicks: {
                    show: true,
                  },
                  title: {
                    text: 'Lane km',
                  },
                  tooltip: {
                    enabled: false,
                  },
                  labels: {
                    formatter(y) {
                      if (typeof y !== 'undefined' && y !== null) {
                        return `${y.toFixed(2)}km`;
                      }
                      return y;
                    },
                  },
                },
              ],
              grid: {
                padding: {
                  left: 20,
                },
              },
              dataLabels: {
                enabled: false,
              },
            }
          }
          series={bridgeRenewalsAndNewBridgesData}
          type="bar"
          width="280"
          height="280"
        />
        <Chart
          options={
            {
              title: {
                text: 'New & improved roads (lane km)',
              },
              chart: {
                animations: {
                  enabled: false,
                },
              },
              xaxis: {
                categories: newAndImprovedRoadsCategories,
                labels: {
                  offsetY: 5,
                  rotate: -45,
                  rotateAlways: true,
                },
                tickPlacement: 'between',
              },
              yaxis: [
                {
                  axisTicks: {
                    show: true,
                  },
                  title: {
                    text: 'Lane km',
                  },
                  tooltip: {
                    enabled: false,
                  },
                  labels: {
                    formatter(y) {
                      if (typeof y !== 'undefined' && y !== null) {
                        return `${y.toFixed(1)}km`;
                      }
                      return y;
                    },
                  },
                },
              ],
              grid: {
                padding: {
                  left: 20,
                },
              },
              dataLabels: {
                enabled: false,
              },
            }
          }
          series={newAndImprovedRoadsData}
          type="bar"
          width="280"
          height="280"
        />
      </Wrap>
      <GroupedRegionMessage
        rcaIds={[1014, 1015, 1016]}
        roadTypes={['shlr', 'sh']}
        messagePrefix="Combined results for"
        messageSuffix="for State Highways and All Roads"
      />
    </ReportSection>
  );
}

export default RoadMaintenanceDeliveryOtherWorks;
